// packages/core/src/schedule/types.ts
//
// Module pur (aucun import React, aucun accès DOM) pour l'éditeur d'horaires
// V2 — cf. Docs/SPECS/2026-09-12-editeur-horaires-v2-decisions.md.
//
// Vocabulaire (D1) : une **plage** (`Range`) est un intervalle [start, end)
// en minutes depuis minuit, peint sur UN jour. Une plage devient une option
// de vote. Le **pas** (`Step`) est la finesse de la grille d'accroche pour
// les gestes pointeur — il ne borne PAS les plages elles-mêmes (D4 : durée
// mini 15 min quel que soit le pas).
//
// Ce fichier ne contient que des types ; la logique est dans intervals.ts,
// geometry.ts, storage.ts et step.ts.

/** Un pas de grille proposé à l'utilisateur (D7), en minutes. */
export const STEP_VALUES = [15, 30, 60, 120, 240] as const;
export type Step = (typeof STEP_VALUES)[number];

/** Nombre de minutes dans une journée — bornes D4/D5 (00:00-24:00, jamais au-delà). */
export const DAY_MINUTES = 24 * 60;

/** Durée minimale d'une plage, quel que soit le pas (D4, D8). */
export const MIN_DURATION_MINUTES = 15;

/**
 * Une plage horaire sur un jour donné.
 * Invariant maintenu par intervals.ts : `0 <= start < end <= DAY_MINUTES`,
 * `end - start >= MIN_DURATION_MINUTES`, `start` et `end` multiples de 15.
 */
export interface Range {
  /** Minute de début depuis minuit, incluse. */
  start: number;
  /** Minute de fin depuis minuit, exclue. */
  end: number;
}

/** Les plages de l'éditeur, par date ISO (YYYY-MM-DD). */
export type DayRangesMap = Record<string, Range[]>;

/**
 * Format de stockage V1 (`timeSlotsByDate`), inchangé pour rester compatible
 * avec `buildPollOptions` (packages/core/src/lib/buildPollOptions.ts) et son
 * port Deno (supabase/functions/_shared/resolve-date-options.ts). V2 lit ce
 * format en entrée (`fromStorage`, sondages V1 existants compris, D23) et
 * l'écrit en sortie (`toStorage`, toujours avec un `duration` explicite par
 * créneau et `mergeMode: false`, D34a).
 */
export interface StoredSlot {
  hour: number;
  minute: number;
  enabled: boolean;
  /** Durée en minutes. Absent en V1 « modèle cellule » → repli sur la granularité. */
  duration?: number;
  /** Libellé "HH:MM" — `toStorage` le renseigne toujours pour rester assignable au `TimeSlot` V1 (PollCreatorService.ts), qui le déclare requis. */
  label?: string;
}

export type StoredTimeSlotsByDate = Record<string, StoredSlot[]>;

/** Résultat d'une tentative d'ajout/déplacement de plage. */
export type RangeOpResult =
  | { ok: true; range: Range }
  | {
      ok: false;
      reason: "overlap" | "too-short" | "out-of-bounds" | "inverted" | "misaligned";
    };
